import {
  Component,
  useEffect,
  useState,
  type ErrorInfo,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Badge,
  Button,
  EmptyState,
  Icon,
  Modal,
  Notice,
} from "./components/ui";
import { useDemo } from "./lib/demo";
import { api } from "../lib/api.ts";
import type { Permission } from "../lib/api.ts";
import { qk } from "../lib/useApi.ts";
import { useDemoMode, usePermissions, useSession } from "../lib/useApi.ts";
import { useQueryClient } from "@tanstack/react-query";
import { Overview } from "./pages/Overview";
import { Setup } from "./pages/Setup";
import { OperationsPage } from "./pages/Operations";
import { ConfigurePage } from "./pages/Configure";
import { InsightsPage } from "./pages/Insights";
type NavItem = { label: string; to: string; icon: string; group: string; permission?: Permission };

// `permission` is the READ permission that gates the destination (doctrine 5). Items without one
// are visible to any signed-in role. Server enforcement still applies; this only hides what a role
// cannot use so it never lands on a forbidden screen by clicking the nav.
const navigation: NavItem[] = [
  { label: "Overview", to: "/", icon: "radio", group: "Operate" },
  { label: "Calls", to: "/calls", icon: "phone", group: "Operate", permission: "calls.read" },
  { label: "Escalations", to: "/escalations", icon: "bell", group: "Operate", permission: "escalation.read" },
  { label: "Proposals", to: "/proposals", icon: "shield", group: "Operate", permission: "proposals.read" },
  { label: "Inbox", to: "/inbox", icon: "inbox", group: "Operate", permission: "inbox.read" },
  { label: "Outage", to: "/outage", icon: "network", group: "Operate", permission: "outage.read" },
  { label: "Policies", to: "/policies", icon: "sliders", group: "Configure", permission: "policy.read" },
  { label: "Flows", to: "/flows", icon: "flow", group: "Configure", permission: "policy.read" },
  { label: "Voices", to: "/voices", icon: "audio", group: "Configure", permission: "policy.read" },
  { label: "Integrations", to: "/integrations", icon: "link", group: "Configure", permission: "integrations.read" },
  { label: "Settings", to: "/settings", icon: "settings", group: "Configure", permission: "users.read" },
  { label: "Reports", to: "/reports", icon: "chart", group: "Understand", permission: "calls.read" },
  { label: "Billing", to: "/billing", icon: "billing", group: "Understand", permission: "billing.read" },
  { label: "Manual", to: "/manual", icon: "help", group: "Understand" },
];
class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: boolean }
> {
  override state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("NightDesk render error", error, info);
  }
  override render() {
    return this.state.error ? (
      <EmptyState
        title="We couldn’t open this screen"
        text="Reload to restore the local demo."
        action={
          <Button onClick={() => window.location.reload()}>Reload</Button>
        }
      />
    ) : (
      this.props.children
    );
  }
}
function PreviewBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const preview = new URLSearchParams(location.search).get("preview");
  if (preview === "loading")
    return (
      <div aria-label="Loading screen" role="status" className="skeleton-page">
        <div />
        <div />
        <div />
      </div>
    );
  if (preview === "empty")
    return (
      <EmptyState
        title="A quiet night"
        text="There are no records in this view yet."
        icon="moon"
      />
    );
  if (preview === "error")
    return (
      <EmptyState
        title="We couldn’t load this view"
        text="Your last confirmed service state has not changed."
        action={
          <Link className="button" to={location.pathname}>
            Try again
          </Link>
        }
      />
    );
  if (preview === "denied")
    return (
      <EmptyState
        title="You don’t have access to this view"
        icon="lock"
        text="Ask your workspace owner to update your permissions."
      />
    );
  return <>{children}</>;
}
export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, notices, dismiss } = useDemo();
  const { can } = usePermissions();
  const session = useSession();
  const { demo } = useDemoMode();
  const queryClient = useQueryClient();
  const visibleNav = navigation.filter((n) => !n.permission || can(n.permission));
  const visibleGroups = ["Operate", "Configure", "Understand"].filter((g) =>
    visibleNav.some((n) => n.group === g),
  );
  const workspaceName = session?.tenant.name ?? state.brand.name;
  const initials =
    session?.user.name
      ?.split(/\s+/)
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "ND";
  async function signOut() {
    try {
      await api.logout();
    } catch {
      /* Even if the network call fails, drop the cached session and show login. */
    }
    queryClient.setQueryData(qk.me, undefined);
    void queryClient.invalidateQueries({ queryKey: qk.me });
    navigate("/login", { replace: true });
  }
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [kiosk, setKiosk] = useState(false);
  const [theme, setTheme] = useState(() => {
    try {
      return typeof window !== "undefined" &&
        localStorage.getItem("nightdesk-theme") === "dark"
        ? "dark"
        : "light";
    } catch {
      return "light";
    }
  });
  const configureActive =
    /^\/(policies|flows|voices|integrations|settings)/.test(location.pathname);
  useEffect(() => {
    setMenu(false);
    setSearch(false);
    const label =
      navigation.find((n) => n.to !== "/" && location.pathname.startsWith(n.to))
        ?.label || "Overview";
    document.title = `${label} · NightDesk`;
  }, [location.pathname]);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("nightdesk-theme", theme);
    } catch {
      /* Private or restricted storage: preference is session-only. */
    }
  }, [theme]);
  useEffect(() => {
    function key(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearch((v) => !v);
      }
      if (
        e.key === "Escape" &&
        !document.fullscreenElement &&
        !document.querySelector('[role="dialog"]')
      )
        setKiosk(false);
    }
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, []);
  useEffect(() => {
    const syncFullscreen = () => {
      if (!document.fullscreenElement) setKiosk(false);
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);
  useEffect(() => {
    if (!kiosk) return;
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => navigate("/"), 120_000);
    };
    arm();
    window.addEventListener("pointerdown", arm);
    window.addEventListener("keydown", arm);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
    };
  }, [kiosk, navigate]);
  async function toggleDisplay() {
    const value = !kiosk;
    setKiosk(value);
    if (value) {
      navigate("/");
      try {
        await document.documentElement.requestFullscreen();
      } catch {
        /* Display layout still works when fullscreen is unavailable. */
      }
    } else if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch {
        /* Browser may already have exited. */
      }
    }
  }
  return (
    <div className={`app ${kiosk ? "kiosk" : ""}`}>
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <header className="app-header">
        <Link className="brand" to="/" aria-label="NightDesk overview">
          <span className="brand-moon" />
          <span>nightdesk</span>
        </Link>
        <nav className="top-nav" aria-label="Primary">
          <NavLink to="/" end>
            <Icon name="radio" />
            Operate
          </NavLink>
          <NavLink to="/calls">
            <Icon name="phone" />
            Calls
          </NavLink>
          <NavLink to="/inbox">
            <Icon name="inbox" />
            Inbox
          </NavLink>
          {can("policy.read") && (
            <NavLink to="/setup">
              <Icon name="success" />
              Setup
            </NavLink>
          )}
          {can("policy.read") && (
            <NavLink to="/policies" className={configureActive ? "active" : ""}>
              <Icon name="sliders" />
              Configure
            </NavLink>
          )}
        </nav>
        <div className="header-tools">
          <button className="workspace-button" onClick={() => setMenu(true)}>
            {workspaceName} <Icon name="down" size={16} />
          </button>
          <button
            className="icon-button search-trigger"
            aria-label="Search NightDesk"
            onClick={() => setSearch(true)}
          >
            <Icon name="search" />
          </button>
          <button
            className="avatar"
            onClick={() => setMenu(true)}
            aria-label="Open account and navigation"
          >
            {initials}
          </button>
        </div>
      </header>
      {demo && (
        <div className="demo-banner">
          <Icon name="info" size={18} />
          <span>
            Demo mode{" "}
            <span className="banner-detail">· calls, texts, and pages are simulated</span>
          </span>
          <Link to="/settings">Manage</Link>
        </div>
      )}
      {kiosk && (
        <div className="display-toolbar">
          <Badge tone="blue">Display mode</Badge>
          <span>Returns to Overview after 2 minutes idle</span>
          <Button onClick={toggleDisplay} icon="fullscreen">
            Exit display
          </Button>
        </div>
      )}
      <main id="main-content" className="app-main" tabIndex={-1}>
        <ErrorBoundary key={location.pathname}>
          <PreviewBoundary>
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/setup" element={<Setup />} />
              <Route path="/calls" element={<OperationsPage page="calls" />} />
              <Route
                path="/calls/:id"
                element={<OperationsPage page="call" />}
              />
              <Route
                path="/escalations"
                element={<OperationsPage page="escalations" />}
              />
              <Route
                path="/escalations/:id"
                element={<OperationsPage page="escalation" />}
              />
              <Route
                path="/proposals"
                element={<OperationsPage page="proposals" />}
              />
              <Route path="/inbox" element={<OperationsPage page="inbox" />} />
              <Route
                path="/outage"
                element={<OperationsPage page="outage" />}
              />
              <Route
                path="/policies"
                element={<ConfigurePage page="policies" />}
              />
              <Route path="/flows" element={<ConfigurePage page="flows" />} />
              <Route
                path="/flows/:id"
                element={<ConfigurePage page="flow" />}
              />
              <Route path="/voices" element={<ConfigurePage page="voices" />} />
              <Route path="/oncall" element={<ConfigurePage page="oncall" />} />
              <Route path="/people" element={<ConfigurePage page="people" />} />
              <Route
                path="/integrations"
                element={<ConfigurePage page="integrations" />}
              />
              <Route
                path="/settings"
                element={<ConfigurePage page="settings" />}
              />
              <Route
                path="/reports"
                element={<InsightsPage page="reports" />}
              />
              <Route
                path="/billing"
                element={<InsightsPage page="billing" />}
              />
              <Route path="/manual" element={<InsightsPage page="manual" />} />
              <Route
                path="/manual/:slug"
                element={<InsightsPage page="manual" />}
              />
              {/* Internal design-system reference — not part of the shipped product. */}
              <Route path="/design-system" element={<Navigate to="/" replace />} />
              <Route
                path="*"
                element={
                  <EmptyState
                    title="This page isn’t here"
                    action={
                      <Link to="/" className="button">
                        Back to Overview
                      </Link>
                    }
                  />
                }
              />
            </Routes>
          </PreviewBoundary>
        </ErrorBoundary>
      </main>
      <footer className="app-footer">
        <button className="text-button" onClick={() => setMenu(true)}>
          <Icon name="menu" size={16} />
          All destinations
        </button>
        <Link to="/manual">Help & manual</Link>
        <button className="text-button" onClick={toggleDisplay}>
          <Icon name="fullscreen" size={16} />
          Display mode
        </button>
        <button
          className="text-button"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
          {theme === "dark" ? "Light" : "Dark"} appearance
        </button>
      </footer>
      <nav className="bottom-nav" aria-label="Mobile navigation">
        <NavLink to="/" end>
          <Icon name="radio" />
          Operate
        </NavLink>
        {can("calls.read") && (
          <NavLink to="/calls">
            <Icon name="phone" />
            Calls
          </NavLink>
        )}
        {can("inbox.read") && (
          <NavLink to="/inbox">
            <Icon name="inbox" />
            Inbox
          </NavLink>
        )}
        <button onClick={() => setMenu(true)} aria-expanded={menu}>
          <Icon name="more" />
          More
        </button>
      </nav>
      <Modal
        open={menu}
        onClose={() => setMenu(false)}
        title="Your workspace"
        description={session ? `${workspaceName}${session.user.name ? ` · ${session.user.name}` : ""}` : workspaceName}
        sheet
      >
        <nav className="destination-list" aria-label="All destinations">
          {visibleGroups.map((group) => (
            <div key={group}>
              <span className="eyebrow">{group}</span>
              {visibleNav
                .filter((n) => n.group === group)
                .map((n) => (
                  <NavLink
                    to={n.to}
                    key={n.to}
                    end
                    onClick={() => setMenu(false)}
                  >
                    <Icon name={n.icon} />
                    {n.label}
                    <Icon name="chevron" size={16} />
                  </NavLink>
                ))}
            </div>
          ))}
          <div>
            <span className="eyebrow">Account</span>
            <button
              type="button"
              className="destination-signout"
              onClick={() => {
                setMenu(false);
                void signOut();
              }}
            >
              <Icon name="lock" />
              Sign out
              <Icon name="arrow" size={16} />
            </button>
          </div>
        </nav>
      </Modal>
      <Modal
        open={search}
        onClose={() => setSearch(false)}
        title="Find a screen"
        description="Search NightDesk destinations"
      >
        <div className="search-field">
          <Icon name="search" />
          <input
            autoFocus
            placeholder="Calls, flows, voices…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search destinations"
          />
        </div>
        <div className="destination-list">
          {visibleNav
            .filter((n) => n.label.toLowerCase().includes(query.toLowerCase()))
            .map((n) => (
              <Link to={n.to} key={n.to} onClick={() => setSearch(false)}>
                <Icon name={n.icon} />
                {n.label}
                <Icon name="arrow" size={16} />
              </Link>
            ))}
        </div>
      </Modal>
      <div className="toast-region" aria-live="polite" aria-atomic="false">
        {notices.map((n) => (
          <div
            className={`toast ${n.error ? "toast-error" : ""}`}
            key={n.id}
            role="status"
          >
            <Icon name={n.error ? "info" : "success"} />
            <span>{n.text}</span>
            <button
              aria-label="Dismiss notification"
              onClick={() => dismiss(n.id)}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
